import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { domain } from "../../../security";
import { format, isToday, isYesterday } from "date-fns";
import {
  Activity,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  User,
  Tag,
  Layers,
  ArrowUpDown,
  SlidersHorizontal,
  Eye,
  RefreshCw,
  CheckCircle2,
  PlusCircle,
  Pencil,
  Trash2,
  RotateCcw,
  ShieldCheck,
  XCircle,
  Ban,
  Send,
  Package,
  Loader2,
} from "lucide-react";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ACTION_CONFIG = {
  Created: {
    color: "#22c55e",
    bg: "#f0fdf4",
    border: "#bbf7d0",
    icon: PlusCircle,
  },
  Updated: { color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe", icon: Pencil },
  Deleted: { color: "#ef4444", bg: "#fef2f2", border: "#fecaca", icon: Trash2 },
  Restored: {
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    icon: RotateCcw,
  },
  Approved: {
    color: "#10b981",
    bg: "#ecfdf5",
    border: "#a7f3d0",
    icon: CheckCircle2,
  },
  Rejected: {
    color: "#f97316",
    bg: "#fff7ed",
    border: "#fed7aa",
    icon: XCircle,
  },
  Voided: { color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: Ban },
  Reverted: {
    color: "#8b5cf6",
    bg: "#f5f3ff",
    border: "#ddd6fe",
    icon: RotateCcw,
  },
  Received: {
    color: "#06b6d4",
    bg: "#ecfeff",
    border: "#a5f3fc",
    icon: Package,
  },
  Cancelled: { color: "#ef4444", bg: "#fef2f2", border: "#fecaca", icon: X },
  Transferred: {
    color: "#f59e0b",
    bg: "#fffbeb",
    border: "#fde68a",
    icon: Send,
  },
  RequestedApproval: {
    color: "#6366f1",
    bg: "#eef2ff",
    border: "#c7d2fe",
    icon: ShieldCheck,
  },
};

const getActionConfig = (action) =>
  ACTION_CONFIG[action] ?? {
    color: "#64748b",
    bg: "#f8fafc",
    border: "#e2e8f0",
    icon: Activity,
  };

const formatTimestamp = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isToday(d)) return "Today, " + format(d, "h:mm a");
  if (isYesterday(d)) return "Yesterday, " + format(d, "h:mm a");
  return format(d, "MMM d, yyyy · h:mm a");
};

const formatJsonDisplay = (obj) => {
  if (!obj) return null;
  try {
    return JSON.stringify(obj, null, 2);
  } catch {
    return String(obj);
  }
};

// ─── Detail Modal ─────────────────────────────────────────────────────────────

const DetailModal = ({ logId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("after"); // "before" | "after"

  useEffect(() => {
    if (!logId) return;
    setLoading(true);
    axios
      .get(`${domain}/api/AuditLogs/${logId}`)
      .then((r) => {
        setData(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [logId]);

  if (!logId) return null;

  const cfg = data ? getActionConfig(data.action) : getActionConfig("Created");
  const ActionIcon = cfg.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden"
        style={{ border: "1px solid #e2e8f0" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            {loading ? (
              <Loader2 size={20} className="animate-spin text-slate-400" />
            ) : (
              <div
                className="p-2 rounded-xl"
                style={{
                  background: cfg.bg,
                  border: `1px solid ${cfg.border}`,
                }}
              >
                <ActionIcon size={18} style={{ color: cfg.color }} />
              </div>
            )}
            <div>
              <p className="font-bold text-slate-800 text-sm leading-none">
                {loading ? "Loading…" : (data?.summary ?? "Activity Detail")}
              </p>
              {data && (
                <p className="text-xs text-slate-400 mt-1">
                  {formatTimestamp(data.timestamp)}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin text-slate-300" />
          </div>
        ) : !data ? (
          <div className="flex-1 flex items-center justify-center py-20 text-slate-400 text-sm">
            Failed to load detail.
          </div>
        ) : (
          <>
            {/* Meta Pills */}
            <div className="px-6 py-3 flex flex-wrap gap-2 border-b border-slate-50 bg-slate-50/60">
              {[
                { icon: Layers, label: "Module", value: data.module },
                {
                  icon: Tag,
                  label: "Action",
                  value: data.action,
                  color: cfg.color,
                },
                { icon: User, label: "By", value: data.userName ?? "System" },
                {
                  icon: Clock,
                  label: "When",
                  value: formatTimestamp(data.timestamp),
                },
              ].map(({ icon: Icon, label, value, color }) => (
                <div
                  key={label}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
                  style={{ background: "#fff", border: "1px solid #e2e8f0" }}
                >
                  <Icon size={12} style={{ color: color ?? "#94a3b8" }} />
                  <span className="text-slate-400">{label}:</span>
                  <span className="text-slate-700" style={{ color: color }}>
                    {value}
                  </span>
                </div>
              ))}
            </div>

            {/* Tabs */}
            {(data.before || data.after) && (
              <div className="flex border-b border-slate-100 px-6">
                {data.before && (
                  <button
                    onClick={() => setTab("before")}
                    className={`py-3 px-4 text-sm font-semibold border-b-2 transition ${
                      tab === "before"
                        ? "border-amber-500 text-amber-600"
                        : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    Before
                  </button>
                )}
                {data.after && (
                  <button
                    onClick={() => setTab("after")}
                    className={`py-3 px-4 text-sm font-semibold border-b-2 transition ${
                      tab === "after"
                        ? "border-indigo-500 text-indigo-600"
                        : "border-transparent text-slate-400 hover:text-slate-600"
                    }`}
                  >
                    After
                  </button>
                )}
              </div>
            )}

            {/* JSON Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {!data.before && !data.after ? (
                <div className="text-center text-slate-400 text-sm py-10">
                  No snapshot data available.
                </div>
              ) : (
                <pre
                  className="text-xs leading-relaxed rounded-xl p-5 overflow-x-auto font-mono"
                  style={{
                    background: tab === "before" ? "#fffbeb" : "#f0f9ff",
                    border: `1px solid ${tab === "before" ? "#fde68a" : "#bae6fd"}`,
                    color: "#1e293b",
                  }}
                >
                  {formatJsonDisplay(
                    tab === "before" ? data.before : data.after,
                  ) ?? "—"}
                </pre>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Filter Bar ───────────────────────────────────────────────────────────────

const FilterBar = ({ filters, setFilters, filterOptions, onReset }) => {
  const [open, setOpen] = useState(false);

  const activeCount = [
    filters.module,
    filters.action,
    filters.userId,
    filters.dateFrom,
    filters.dateTo,
  ].filter(Boolean).length;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition border ${
          activeCount > 0
            ? "bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200"
            : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
        }`}
      >
        <SlidersHorizontal size={16} />
        Filters
        {activeCount > 0 && (
          <span className="bg-white/30 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 z-40 bg-white rounded-2xl shadow-xl p-5 w-80 flex flex-col gap-4"
          style={{ border: "1px solid #e2e8f0" }}
        >
          <div className="flex items-center justify-between">
            <span className="font-bold text-slate-800 text-sm">
              Filter Activity
            </span>
            <button
              onClick={() => {
                onReset();
                setOpen(false);
              }}
              className="text-xs text-indigo-600 font-semibold hover:underline"
            >
              Reset all
            </button>
          </div>

          {/* Module */}
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">
              Module
            </label>
            <select
              value={filters.module}
              onChange={(e) =>
                setFilters((f) => ({ ...f, module: e.target.value }))
              }
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none"
            >
              <option value="">All Modules</option>
              {filterOptions.modules.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>

          {/* Action */}
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">
              Action
            </label>
            <select
              value={filters.action}
              onChange={(e) =>
                setFilters((f) => ({ ...f, action: e.target.value }))
              }
              className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none"
            >
              <option value="">All Actions</option>
              {filterOptions.actions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">
                From
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, dateFrom: e.target.value }))
                }
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1.5 block uppercase tracking-wider">
                To
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, dateTo: e.target.value }))
                }
                className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-300 outline-none"
              />
            </div>
          </div>

          <button
            onClick={() => setOpen(false)}
            className="w-full bg-indigo-600 text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-indigo-700 transition"
          >
            Apply Filters
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Activity Row ─────────────────────────────────────────────────────────────

const ActivityRow = ({ log, onViewDetail }) => {
  const cfg = getActionConfig(log.action);
  const ActionIcon = cfg.icon;

  return (
    <tr
      className="group cursor-pointer hover:bg-slate-50/80 transition"
      onClick={() => onViewDetail(log.id)}
    >
      {/* Action Badge */}
      <td className="pl-6 pr-3 py-4 whitespace-nowrap">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold"
          style={{
            background: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
          }}
        >
          <ActionIcon size={12} />
          {log.action}
        </div>
      </td>

      {/* Module */}
      <td className="px-3 py-4 whitespace-nowrap">
        <span
          className="text-xs font-semibold px-2.5 py-1 rounded-lg"
          style={{ background: "#f1f5f9", color: "#475569" }}
        >
          {log.module}
        </span>
      </td>

      {/* Entity Label */}
      <td className="px-3 py-4">
        <span className="text-sm font-semibold text-slate-800">
          {log.entityLabel ?? (
            <span className="text-slate-400 font-normal">—</span>
          )}
        </span>
        {log.entityId && (
          <span className="block text-xs text-slate-400 font-mono mt-0.5">
            #{log.entityId}
          </span>
        )}
      </td>

      {/* Who */}
      <td className="px-3 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
            style={{ background: "#e0e7ff", color: "#4f46e5" }}
          >
            {(log.userName ?? "S").charAt(0).toUpperCase()}
          </div>
          <span className="text-sm text-slate-700 font-medium">
            {log.userName ?? "System"}
          </span>
        </div>
      </td>

      {/* When */}
      <td className="px-3 py-4 whitespace-nowrap">
        <span className="text-xs text-slate-500 font-medium">
          {formatTimestamp(log.timestamp)}
        </span>
      </td>

      {/* Snapshot indicators + View */}
      <td className="px-3 pr-6 py-4 whitespace-nowrap">
        <div className="flex items-center justify-end gap-2">
          {log.hasBefore && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
              BEFORE
            </span>
          )}
          {log.hasAfter && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-200">
              AFTER
            </span>
          )}
          <button
            className="ml-1 flex items-center gap-1.5 text-xs font-semibold text-indigo-600 opacity-0 group-hover:opacity-100 transition px-3 py-1.5 rounded-lg bg-indigo-50 border border-indigo-100"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetail(log.id);
            }}
          >
            <Eye size={13} /> View
          </button>
        </div>
      </td>
    </tr>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    modules: [],
    actions: [],
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortDesc, setSortDesc] = useState(true);
  const [sortBy, setSortBy] = useState("timestamp");

  const [filters, setFilters] = useState({
    module: "",
    action: "",
    userId: "",
    dateFrom: "",
    dateTo: "",
  });

  // Fetch filter options once
  useEffect(() => {
    axios
      .get(`${domain}/api/AuditLogs/filters`)
      .then((r) => setFilterOptions(r.data))
      .catch(() => {});
  }, []);

  // Fetch logs whenever any query param changes
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        pageSize,
        sortBy,
        sortDesc,
        ...(search && { search }),
        ...(filters.module && { module: filters.module }),
        ...(filters.action && { action: filters.action }),
        ...(filters.userId && { userId: filters.userId }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo }),
      });
      const res = await axios.get(`${domain}/api/AuditLogs?${params}`);
      setLogs(res.data.data);
      setTotalCount(res.data.totalCount);
      setTotalPages(res.data.totalPages);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, sortBy, sortDesc, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const resetFilters = () => {
    setFilters({
      module: "",
      action: "",
      userId: "",
      dateFrom: "",
      dateTo: "",
    });
    setPage(1);
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDesc((d) => !d);
    else {
      setSortBy(col);
      setSortDesc(true);
    }
    setPage(1);
  };

  const SortTh = ({ col, label }) => (
    <th
      className="px-3 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none whitespace-nowrap"
      onClick={() => toggleSort(col)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown
          size={12}
          className={sortBy === col ? "text-indigo-500" : "text-slate-300"}
        />
      </span>
    </th>
  );

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="min-h-screen ">
      {/* ── Page Header ── */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-screen-xl mx-auto px-6 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-600 rounded-xl shadow-md shadow-indigo-200">
              <Activity size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-extrabold text-slate-900 leading-none tracking-tight">
                Activity Feed
              </h1>
              <p className="text-xs text-slate-400 mt-0.5">
                Complete audit trail across all modules
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search activity…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none transition-all w-64"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Refresh */}
            <button
              onClick={fetchLogs}
              className="p-2.5 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-100 transition"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>

            {/* Filters */}
            <FilterBar
              filters={filters}
              setFilters={(f) => {
                setFilters(f);
                setPage(1);
              }}
              filterOptions={filterOptions}
              onReset={resetFilters}
            />
          </div>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="max-w-screen-xl mx-auto px-6 pt-5 pb-2 flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <span className="font-bold text-slate-800 text-xl">
            {totalCount.toLocaleString()}
          </span>
          total records
        </div>
        {activeFilterCount > 0 && (
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Filter size={12} className="text-indigo-400" />
            <span>
              {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}{" "}
              active
            </span>
            <button
              onClick={resetFilters}
              className="text-indigo-600 font-semibold hover:underline"
            >
              Clear
            </button>
          </div>
        )}
        <div className="ml-auto text-xs text-slate-400">
          Page {page} of {totalPages}
        </div>
      </div>

      {/* ── Table ── */}
      <div className="max-w-screen-xl mx-auto px-6 pb-10">
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{
            border: "1px solid #e2e8f0",
            boxShadow: "0 1px 3px rgba(0,0,0,.04), 0 8px 24px rgba(0,0,0,.04)",
          }}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="border-b border-slate-100">
                <tr style={{ background: "#f8fafc" }}>
                  <SortTh col="action" label="Action" />
                  <SortTh col="module" label="Module" />
                  <th className="px-3 py-3.5 text-left text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Record
                  </th>
                  <SortTh col="user" label="By" />
                  <SortTh col="timestamp" label="When" />
                  <th className="px-3 pr-6 py-3.5 text-right text-xs font-bold text-slate-500 uppercase tracking-wider">
                    Snapshot
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-50">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-slate-400">
                        <Loader2
                          size={28}
                          className="animate-spin text-indigo-300"
                        />
                        <span className="text-sm">Loading activity…</span>
                      </div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="p-4 bg-slate-100 rounded-full">
                          <Activity size={24} className="text-slate-300" />
                        </div>
                        <p className="text-slate-400 text-sm font-medium">
                          No activity found.
                        </p>
                        {(search || activeFilterCount > 0) && (
                          <button
                            onClick={() => {
                              setSearchInput("");
                              resetFilters();
                            }}
                            className="text-indigo-600 text-xs font-semibold hover:underline"
                          >
                            Clear search & filters
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <ActivityRow
                      key={log.id}
                      log={log}
                      onViewDetail={setSelectedId}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {totalPages > 1 && (
            <div
              className="flex items-center justify-between px-6 py-4 border-t border-slate-100"
              style={{ background: "#fafafa" }}
            >
              <span className="text-xs text-slate-400 font-medium">
                Showing {(page - 1) * pageSize + 1}–
                {Math.min(page * pageSize, totalCount)} of{" "}
                {totalCount.toLocaleString()}
              </span>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition"
                >
                  First
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Page number pills */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let p;
                  if (totalPages <= 5) p = i + 1;
                  else if (page <= 3) p = i + 1;
                  else if (page >= totalPages - 2) p = totalPages - 4 + i;
                  else p = page - 2 + i;
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 text-xs font-bold rounded-lg transition ${
                        page === p
                          ? "bg-indigo-600 text-white shadow-md shadow-indigo-200"
                          : "border border-slate-200 text-slate-500 hover:bg-slate-100"
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  <ChevronRight size={16} />
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed font-medium transition"
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Detail Modal ── */}
      <DetailModal logId={selectedId} onClose={() => setSelectedId(null)} />
    </div>
  );
};

export default AuditLogs;
